import React, { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import Logo from "@/components/Logo";
import { toast } from "sonner";
import { Loader2, ArrowLeft } from "lucide-react";

export default function ResetPassword() {
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const navigate = useNavigate();
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (password.length < 8) return setError("Password must be at least 8 characters.");
    if (password !== confirm) return setError("Passwords do not match.");
    setLoading(true);
    try {
      await api.post("/auth/reset-password", { token, new_password: password });
      toast.success("Password updated. Please sign in with your new password.");
      navigate("/login");
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || "Unable to reset password.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-ivory px-6 py-12">
      <div className="w-full max-w-md">
        <Logo />
        <div className="mt-12">
          <h1 className="font-serif text-4xl font-bold tracking-tight text-navy-900">Set a new password</h1>
          <p className="mt-3 text-navy-500">Choose a new password for your RCC Connect admin account.</p>
        </div>

        {!token ? (
          <div className="mt-10 rounded-xl bg-destructive/10 px-4 py-3 text-sm text-destructive" data-testid="reset-no-token">
            This reset link is missing its token. Please request a new link from the sign-in page.
          </div>
        ) : (
          <form onSubmit={submit} className="mt-10 space-y-5">
            <div>
              <Label htmlFor="password" className="text-navy-800">New password</Label>
              <Input
                id="password"
                type="password"
                data-testid="reset-password-input"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="At least 8 characters"
                className="mt-1.5 h-12 rounded-xl border-navy-100 bg-white focus-visible:ring-gold-500"
                required
              />
            </div>
            <div>
              <Label htmlFor="confirm" className="text-navy-800">Confirm new password</Label>
              <Input
                id="confirm"
                type="password"
                data-testid="reset-confirm-input"
                value={confirm}
                onChange={(e) => setConfirm(e.target.value)}
                placeholder="Re-enter your new password"
                className="mt-1.5 h-12 rounded-xl border-navy-100 bg-white focus-visible:ring-gold-500"
                required
              />
            </div>
            {error && (
              <div data-testid="reset-error" className="rounded-xl bg-destructive/10 px-4 py-3 text-sm text-destructive">
                {error}
              </div>
            )}
            <Button
              type="submit"
              data-testid="reset-submit-button"
              disabled={loading}
              className="h-12 w-full rounded-xl bg-gold-500 text-white hover:bg-gold-600 font-semibold text-base shadow-ambient"
            >
              {loading ? <Loader2 className="h-5 w-5 animate-spin" /> : "Update password"}
            </Button>
          </form>
        )}

        <Link to="/login" className="mt-8 inline-flex items-center gap-2 text-sm font-semibold text-gold-600 hover:text-gold-700">
          <ArrowLeft className="h-4 w-4" /> Back to sign in
        </Link>
      </div>
    </div>
  );
}
